import React from 'react';
import { CheckCircle, Clock, FileText, Phone } from 'lucide-react';

const HowToSchema = () => {
  const howToData = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Serve Legal Documents in California",
    "description": "Complete step-by-step guide to serving legal documents in California, including requirements, timelines, and procedures.",
    "image": "https://legaldocspecialist.com/images/process-serving-guide.jpg",
    "totalTime": "PT3D",
    "supply": [
      {
        "@type": "HowToSupply",
        "name": "Legal Documents to be Served"
      },
      {
        "@type": "HowToSupply", 
        "name": "Process Server Information"
      },
      {
        "@type": "HowToSupply",
        "name": "Recipient Address and Information"
      }
    ],
    "tool": [
      {
        "@type": "HowToTool",
        "name": "Professional Process Server"
      }
    ],
    "step": [
      {
        "@type": "HowToStep",
        "name": "Prepare Your Documents",
        "text": "Gather all legal documents that need to be served and ensure they are properly completed and signed by the court clerk if required.",
        "image": "https://legaldocspecialist.com/images/prepare-documents.jpg",
        "url": "https://legaldocspecialist.com/blog/complete-guide-process-serving-california-2025#prepare"
      },
      {
        "@type": "HowToStep", 
        "name": "Choose Service Level",
        "text": "Select your service level: Routine (4 days), Priority (Next Business Day), Urgent (Same Day), or On-Demand (under 2 hours).",
        "image": "https://legaldocspecialist.com/images/service-levels.jpg",
        "url": "https://legaldocspecialist.com/place-order"
      },
      {
        "@type": "HowToStep",
        "name": "Provide Recipient Information", 
        "text": "Supply complete and accurate information about the person to be served, including full name, address, and physical description if available.",
        "image": "https://legaldocspecialist.com/images/recipient-info.jpg",
        "url": "https://legaldocspecialist.com/place-order"
      },
      {
        "@type": "HowToStep",
        "name": "Professional Service Attempt",
        "text": "Our registered and bonded process server will attempt service at the provided address following all California legal requirements.",
        "image": "https://legaldocspecialist.com/images/service-attempt.jpg"
      },
      {
        "@type": "HowToStep",
        "name": "Receive Proof of Service",
        "text": "Upon successful service, you'll receive a detailed affidavit of service with GPS data, timestamps, and all required legal documentation.",
        "image": "https://legaldocspecialist.com/images/proof-of-service.jpg"
      }
    ]
  };

  React.useEffect(() => {
    // Add HowTo Schema to page
    const existingHowToSchema = document.querySelector('#howto-schema-data');
    if (existingHowToSchema) {
      existingHowToSchema.remove();
    }
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'howto-schema-data';
    script.textContent = JSON.stringify(howToData);
    document.head.appendChild(script);

    return () => {
      const schemaElement = document.querySelector('#howto-schema-data');
      if (schemaElement) {
        schemaElement.remove();
      }
    };
  }, []);

  return (
    <section style={{
      backgroundColor: 'var(--bg-page)',
      padding: '80px 0'
    }}>
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <h2 className="heading-1" style={{ marginBottom: '1rem' }}>
            How to Serve Legal Documents in California
          </h2>
          <p className="body-large" style={{ 
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            Follow our step-by-step process for professional legal document service
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {howToData.step.map((step, index) => {
            const icons = [FileText, Clock, Phone, CheckCircle, CheckCircle];
            const IconComponent = icons[index] || CheckCircle;
            
            return (
              <div 
                key={index}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  padding: '2rem',
                  borderRadius: '12px',
                  border: '1px solid var(--border-medium)',
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-15px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '30px',
                  height: '30px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '0.9rem',
                  fontWeight: '700'
                }}>
                  {index + 1}
                </div>

                <div style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'var(--brand-primary)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.5rem auto'
                }}>
                  <IconComponent size={28} color="white" />
                </div>

                <h3 className="heading-3" style={{
                  marginBottom: '1rem',
                  fontSize: '1.3rem'
                }}>
                  {step.name}
                </h3>
                
                <p className="body-small" style={{
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6'
                }}>
                  {step.text}
                </p>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div style={{
          textAlign: 'center',
          marginTop: '4rem',
          padding: '2rem',
          backgroundColor: 'var(--bg-card)',
          borderRadius: '12px',
          border: '1px solid var(--border-medium)'
        }}>
          <h3 className="heading-3" style={{ marginBottom: '1rem' }}>
            Need Documents Served?
          </h3>
          <p className="body-medium" style={{ 
            color: 'var(--text-secondary)',
            marginBottom: '2rem'
          }}>
            Let our experienced team handle the entire process for you.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a 
              href="/place-order"
              className="btn-primary"
              style={{ textDecoration: 'none' }}
            >
              Start Your Order
            </a>
            <a href="/quote" className="btn-primary" data-testid="howto-get-quote-link" style={{ textDecoration: 'none' }}>
              Get a Quote
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToSchema;